import React, { createContext, useState, useEffect, useCallback, useContext, useRef } from 'react';
import axios from 'axios';
import { AuthContext } from './AuthContext';

export const ListasContext = createContext();

const lerCache = (chave) => {
    try {
        const salvo = localStorage.getItem(chave);
        return salvo ? JSON.parse(salvo) : [];
    } catch (erro) {
        return [];
    }
};

const paraOpcao = (tipo) => (result) => ({
    value: result.id,
    label: result.text,
    ...(tipo && { tipo }),
});

export const ListasProvider = ({ children }) => {
    const [unidadeData, setUnidadeData] = useState(() => {
        const savedData = localStorage.getItem('unidadeData');
        return savedData ? JSON.parse(savedData) : [];
    });

    const [isLoadingUnidade, setIsLoadingUnidade] = useState(false);
    const urlApi = process.env.REACT_APP_URL_API || 'https://return-navy.vercel.app/';
    const { userAcesso, userInfo } = useContext(AuthContext);

    const fetchUnidades = useCallback(async () => {
        setIsLoadingUnidade(true);

        const buscaPagina = (page) => axios.get(urlApi + 'v1/unidades', { params: { page } });
        const converte = (response) => response.data.results
            .filter(result => !result.text.toLowerCase().includes('inativa'))
            .map(result => ({
                value: result.id,
                label: result.text,
                departamento: result.departamento,
                pregadoresResponsaveisIds: result.pregadoresResponsaveisIds || [],
            }));

        try {
            // A 1ª página informa o total; as demais (50 por página) são buscadas em paralelo, mantendo a ordem
            const primeira = await buscaPagina(1);
            const totalPaginas = Math.max(1, Math.ceil(primeira.data.total / 50));
            const demais = await Promise.all(
                Array.from({ length: totalPaginas - 1 }, (_, index) => buscaPagina(index + 2))
            );
            const allUnidades = [primeira, ...demais].flatMap(converte);

            setUnidadeData(allUnidades);
            localStorage.setItem('unidadeData', JSON.stringify(allUnidades));
        } catch (error) {
            console.error('Erro ao buscar unidades - ListasContext');
        } finally {
            setIsLoadingUnidade(false);
        }
    }, [urlApi]);

    useEffect(() => {
        if (userAcesso && unidadeData.length === 0) {
            fetchUnidades();
        }
    }, [fetchUnidades, userAcesso, unidadeData.length]);

    // Listas pequenas dos selects (atividades, motivações e pregadores), compartilhadas por todas as telas.
    // O cache local aparece na hora e é atualizado logo após o login, para os selects já abrirem prontos.
    const [atividadesIndividuais, setAtividadesIndividuais] = useState(() => lerCache('ativInd'));
    const [atividadesColetivas, setAtividadesColetivas] = useState(() => lerCache('ativCol'));
    const [motivacoes, setMotivacoes] = useState(() => lerCache('motivacoes'));
    const [pregadores, setPregadores] = useState(() => lerCache('pregadoresUsuario'));
    const [carregando, setCarregando] = useState({});

    const carregaLista = useCallback(async (nome, chaveCache, setLista, requisicao) => {
        setCarregando((atual) => ({ ...atual, [nome]: true }));
        try {
            const lista = await requisicao();
            setLista(lista);
            localStorage.setItem(chaveCache, JSON.stringify(lista));
        } catch (error) {
            console.error(`Erro ao buscar ${nome} - ListasContext`);
        } finally {
            setCarregando((atual) => ({ ...atual, [nome]: false }));
        }
    }, []);

    const listasGeraisCarregadasRef = useRef(false);
    useEffect(() => {
        if (!userAcesso) {
            listasGeraisCarregadasRef.current = false;
            return;
        }
        if (listasGeraisCarregadasRef.current) return;
        listasGeraisCarregadasRef.current = true;

        const buscaLista = (endpoint, tipo) => async () =>
            (await axios.get(urlApi + endpoint)).data.results.map(paraOpcao(tipo));

        carregaLista('atividadesIndividuais', 'ativInd', setAtividadesIndividuais, buscaLista('v1/atividades_individuais', 'Individual'));
        carregaLista('atividadesColetivas', 'ativCol', setAtividadesColetivas, buscaLista('v1/atividades_coletivas', 'Coletivo'));
        carregaLista('motivacoes', 'motivacoes', setMotivacoes, buscaLista('v1/motivacao'));
    }, [userAcesso, carregaLista, urlApi]);

    // Pregadores do escopo do usuário (todos, com acesso total; senão os das suas unidades), numa chamada só
    const acessoTotal = userInfo?.bitrix?.acessoTotal;
    const unidadesUsuario = userInfo?.bitrix?.unidades;
    const escopoPregadoresRef = useRef(null);
    useEffect(() => {
        if (!userAcesso) {
            escopoPregadoresRef.current = null;
            setPregadores([]);
            return;
        }
        const unidadeIds = (unidadesUsuario || [])
            .map((unidade) => unidade.id || unidade.value)
            .filter(Boolean);
        // Espera o AuthContext definir o acesso (o acessoTotal só fica pronto depois das unidades)
        if (acessoTotal !== true && !(acessoTotal === false && unidadeIds.length > 0)) return;

        const escopo = acessoTotal ? 'todos' : unidadeIds.join(',');
        if (escopoPregadoresRef.current === escopo) return;
        escopoPregadoresRef.current = escopo;

        carregaLista('pregadores', 'pregadoresUsuario', setPregadores, async () => {
            const response = await axios.get(urlApi + 'v1/pregador-responsavel', {
                params: { limit: 500, ...(!acessoTotal && { unidadeId: unidadeIds }) },
            });
            return response.data.results.map(paraOpcao());
        });
    }, [userAcesso, acessoTotal, unidadesUsuario, carregaLista, urlApi]);

    // "Carregando..." no select só enquanto a lista ainda está vazia (com cache, ela já aparece)
    const carregandoLista = (nome, lista) => Boolean(carregando[nome]) && lista.length === 0;

    return (
        <ListasContext.Provider value={{
            unidadeData,
            isLoadingUnidade,
            atividadesIndividuais,
            atividadesColetivas,
            motivacoes,
            pregadores,
            carregandoAtividadesIndividuais: carregandoLista('atividadesIndividuais', atividadesIndividuais),
            carregandoAtividadesColetivas: carregandoLista('atividadesColetivas', atividadesColetivas),
            carregandoMotivacoes: carregandoLista('motivacoes', motivacoes),
            carregandoPregadores: carregandoLista('pregadores', pregadores),
        }}>
            {children}
        </ListasContext.Provider>
    );
};
